import React, { useEffect, useState } from 'react';
import ProfaseModal from '../../../commons/ProfaseModal';
import PageForm, { FormField } from '../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import { getEntity as fetchContacto, updateEntity as updateContacto } from '../../contacto.reducer';
import type { ContactoCrearFormValues } from './GrupoContactoCrearModal';

const ACCENT = '#0ea5e9';

const fields: FormField<ContactoCrearFormValues>[] = [
  { name: 'nombre', label: 'Nombre', required: true, placeholder: 'Nombre' },
  { name: 'apellido', label: 'Apellido', required: true, placeholder: 'Apellido' },
  { name: 'fechaNacimiento', label: 'Fecha de nacimiento', type: 'date' },
  { name: 'telefonoOficinaPrimario', label: 'Tel. oficina', placeholder: 'Tel. oficina' },
  { name: 'telefonoOficinaSecundario', label: 'Tel. oficina 2', placeholder: 'Tel. oficina 2' },
  { name: 'telefonoMovilPrimario', label: 'Tel. móvil', placeholder: 'Tel. móvil' },
  { name: 'telefonoMovilSecundario', label: 'Tel. móvil 2', placeholder: 'Tel. móvil 2' },
  { name: 'correoElectronicoPrimario', label: 'Correo electrónico', type: 'email', placeholder: 'correo@ejemplo.com' },
  { name: 'correoElectronicoSecundario', label: 'Correo electrónico 2', type: 'email', placeholder: 'correo2@ejemplo.com' },
];

function makeEmptyForm(): ContactoCrearFormValues {
  return {
    nombre: '',
    apellido: '',
    fechaNacimiento: '',
    telefonoOficinaPrimario: '',
    telefonoOficinaSecundario: '',
    telefonoMovilPrimario: '',
    telefonoMovilSecundario: '',
    correoElectronicoPrimario: '',
    correoElectronicoSecundario: '',
  };
}

function trimOrEmpty(s: string) {
  return s.trim();
}

function toDateInputValue(v: unknown): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string') {
    const head = v.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  }
  return '';
}

function entityToForm(e: Record<string, unknown>): ContactoCrearFormValues {
  let nombre = String(e.nombre ?? '');
  let apellido = String(e.apellido ?? '');
  if (!nombre.trim() && !apellido.trim() && e.contacto != null) {
    const parts = String(e.contacto).trim().split(/\s+/).filter(Boolean);
    nombre = parts[0] ?? '';
    apellido = parts.slice(1).join(' ');
  }
  return {
    nombre,
    apellido,
    fechaNacimiento: toDateInputValue(e.fechaNacimiento),
    telefonoOficinaPrimario: String(e.telefonoOficinaPrimario ?? ''),
    telefonoOficinaSecundario: String(e.telefonoOficinaSecundario ?? ''),
    telefonoMovilPrimario: String(e.telefonoMovilPrimario ?? ''),
    telefonoMovilSecundario: String(e.telefonoMovilSecundario ?? ''),
    correoElectronicoPrimario: String(e.correoElectronicoPrimario ?? ''),
    correoElectronicoSecundario: String(e.correoElectronicoSecundario ?? ''),
  };
}

export interface GrupoContactoEditarModalProps {
  open: boolean;
  contactId: number | null;
  grupoId?: string | null;
  empresaId?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

export default function GrupoContactoEditarModal({
  open,
  contactId,
  grupoId,
  empresaId,
  onClose,
  onSaved,
}: GrupoContactoEditarModalProps) {
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.contacto.updating);

  const [form, setForm] = useState<ContactoCrearFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof ContactoCrearFormValues, string>>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!open || contactId == null || grupoId == null || grupoId === 'new') {
      setReady(false);
      return;
    }
    let cancelled = false;
    setLoadError(null);
    setErrors({});
    setForm(makeEmptyForm());
    setReady(false);

    dispatch(fetchContacto({ grupoId, id: contactId }))
      .unwrap()
      .then((response: any) => {
        if (cancelled) return;
        const raw = response?.data ?? response;
        setForm(entityToForm((raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>));
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('No se pudo cargar el contacto.');
          setReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, contactId, grupoId, dispatch]);

  const handleFieldChange = (name: keyof ContactoCrearFormValues, value: string) => {
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (contactId == null) return;

    const errs: Partial<Record<keyof ContactoCrearFormValues, string>> = {};
    if (!trimOrEmpty(form.nombre)) errs.nombre = 'El nombre es requerido';
    if (!trimOrEmpty(form.apellido)) errs.apellido = 'El apellido es requerido';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const gid = Number(grupoId);
    if (Number.isNaN(gid)) {
      setErrors({ nombre: 'Grupo no válido' });
      return;
    }

    const nombre = trimOrEmpty(form.nombre);
    const apellido = trimOrEmpty(form.apellido);
    const contactoLabel = [nombre, apellido].filter(Boolean).join(' ');

    const payload: Record<string, unknown> = {
      id: contactId,
      grupoId: gid,
      nombre,
      apellido,
      contacto: contactoLabel,
    };

    if (trimOrEmpty(form.fechaNacimiento)) payload.fechaNacimiento = trimOrEmpty(form.fechaNacimiento);
    if (trimOrEmpty(form.telefonoOficinaPrimario)) payload.telefonoOficinaPrimario = trimOrEmpty(form.telefonoOficinaPrimario);
    if (trimOrEmpty(form.telefonoOficinaSecundario)) payload.telefonoOficinaSecundario = trimOrEmpty(form.telefonoOficinaSecundario);
    if (trimOrEmpty(form.telefonoMovilPrimario)) payload.telefonoMovilPrimario = trimOrEmpty(form.telefonoMovilPrimario);
    if (trimOrEmpty(form.telefonoMovilSecundario)) payload.telefonoMovilSecundario = trimOrEmpty(form.telefonoMovilSecundario);
    if (trimOrEmpty(form.correoElectronicoPrimario)) payload.correoElectronicoPrimario = trimOrEmpty(form.correoElectronicoPrimario);
    if (trimOrEmpty(form.correoElectronicoSecundario)) payload.correoElectronicoSecundario = trimOrEmpty(form.correoElectronicoSecundario);

    const eid = empresaId != null && empresaId !== 'new' ? Number(empresaId) : NaN;
    if (!Number.isNaN(eid)) payload.empresaId = eid;

    try {
      await dispatch(updateContacto(payload)).unwrap();
      onSaved?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open) return null;
  if (grupoId == null || grupoId === 'new' || contactId == null) return null;

  const loading = !ready && !loadError;

  return (
    <ProfaseModal
      open
      titleId="grupo-contacto-editar-modal-title"
      zIndex={100010}
      dialogStyle={{ maxWidth: 560, maxHeight: 'min(90vh, 720px)' }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="grupo-contacto-editar-modal-title" className="profase-modal-title m-0">
          Editar contacto
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">
        Modifique los datos del contacto.
      </p>
      {loadError && (
        <p className="profase-form-alert-error" style={{ margin: '0 0 16px' }} role="alert">
          {loadError}
        </p>
      )}
      {loading && (
        <p className="profase-form-muted-text" style={{ margin: '0 0 16px', fontSize: 14 }}>Cargando…</p>
      )}
      {ready && (
        <PageForm
          embedded
          fields={fields}
          values={form}
          errors={errors}
          isNew={false}
          saving={updating}
          labelInline
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={onClose}
          accentColor={ACCENT}
          submitLabel="Guardar cambios"
          cancelLabel="Cancelar"
        />
      )}
    </ProfaseModal>
  );
}
